// ABOUTME: Pure resolver mapping each constellation/star/edge to a display record.
// ABOUTME: Three orthogonal channels - brightness (attainability), color (affinity filter), emphasis (benefitMatch/conMatch).
import type { Affinity, Constellation, Star, StarId } from "./types";
import type { ReachView } from "./reachability";
import { matchedAffinities } from "./affinity";

export type Brightness = "active" | "attainable" | "unattainable";
export type ColorOutcome = { kind: "identity" } | { kind: "mute" } | { kind: "match"; affinities: Affinity[] };

export interface DisplaySettings {
  selected: Set<StarId>;
  reach?: ReachView;
  affinityFilter?: { grants: Set<Affinity>; requires: Set<Affinity> };
  benefitMatch?: Set<StarId>;
  /** Constellations emphasized by the text search; the constellation-level half of the emphasis channel. */
  conMatch?: Set<string>;
  diff?: { added: Set<StarId>; removed: Set<StarId> } | null;
}

export interface ConstellationDisplay {
  brightness: Brightness;
  color: ColorOutcome;
  selfGlow: boolean;
  emphasis: boolean;
}

// A constellation is active when fully selected, attainable when started or completable
// (or when no reach view is present, the permissive default), else unattainable.
function constellationBrightness(con: Constellation, s: DisplaySettings): Brightness {
  if (con.starIds.length > 0 && con.starIds.every((id) => s.selected.has(id))) return "active";
  if (!s.reach) return "attainable";
  if (con.starIds.some((id) => s.selected.has(id))) return "attainable";
  if (s.reach.completable.has(con.id)) return "attainable";
  return "unattainable";
}

// Color is driven by the affinity filter ALONE: a constellation that provides a filtered
// affinity matches (its matched colors), one that provides none mutes, no filter is identity.
function constellationColor(con: Constellation, s: DisplaySettings): ColorOutcome {
  if (!s.affinityFilter) return { kind: "identity" };
  const matched = matchedAffinities(con, s.affinityFilter.grants, s.affinityFilter.requires);
  return matched.length > 0 ? { kind: "match", affinities: matched } : { kind: "mute" };
}

export function constellationDisplay(con: Constellation, s: DisplaySettings): ConstellationDisplay {
  const brightness = constellationBrightness(con, s);
  return {
    brightness,
    color: constellationColor(con, s),
    selfGlow: brightness === "active",
    emphasis: s.conMatch?.has(con.id) ?? false,
  };
}

export interface StarDisplay {
  brightness: Brightness;
  color: { kind: "mute" } | { kind: "identity" };
  clickable: boolean;
  selected: boolean;
  benefitMatch: boolean;
  diff: "add" | "remove" | null;
}

export function starDisplay(star: Star, con: Constellation, s: DisplaySettings): StarDisplay {
  const selected = s.selected.has(star.id);
  // reachableStars holds every unselected star whose path fits the budget (deep stars of partially
  // enterable constellations included), so it is both the brightness and the click affordance.
  const clickable = !s.reach || s.reach.reachableStars.has(star.id);
  let brightness: Brightness;
  if (selected) brightness = "active";
  else if (clickable) brightness = "attainable";
  else brightness = "unattainable";
  // Stars carry no affinity halo; the affinity axis only mutes them (when their constellation
  // provides none of the filtered colors) or leaves them at identity.
  const conColor = constellationColor(con, s);
  const color: StarDisplay["color"] = conColor.kind === "mute" ? { kind: "mute" } : { kind: "identity" };
  const diff = s.diff ? (s.diff.added.has(star.id) ? "add" : s.diff.removed.has(star.id) ? "remove" : null) : null;
  return { brightness, color, clickable, selected, benefitMatch: s.benefitMatch?.has(star.id) ?? false, diff };
}

export interface EdgeDisplay {
  brightness: Brightness;
  color: { kind: "mute" } | { kind: "identity" };
  taken: boolean;
}

export function edgeDisplay(con: Constellation, fromId: StarId, toId: StarId, s: DisplaySettings): EdgeDisplay {
  const taken = s.selected.has(fromId) && s.selected.has(toId);
  // The deeper endpoint's path contains the shallower one, so the edge sits on a reachable path iff
  // `to` is selected or reachable. Brightness is endpoint-level; the constellation art stays dim.
  const toOnPath = !s.reach || s.selected.has(toId) || s.reach.reachableStars.has(toId);
  const brightness: Brightness = taken ? "active" : toOnPath ? "attainable" : "unattainable";
  const conColor = constellationColor(con, s);
  const color: EdgeDisplay["color"] = conColor.kind === "mute" ? { kind: "mute" } : { kind: "identity" };
  return { brightness, color, taken };
}
